import { JournalEntryItemForm } from './JournalEntryItemForm';
import { Toggle } from './shared/Toggle';
import { JournalStatus } from 'constants/types';
import currency from 'currency.js';
import React from 'react';
import { map } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  currencySum,
  currencyValueFormatter,
  removeProperty,
} from 'utils/generic';
import { DEFAULT_EXCHANGE_RATE } from 'utils/stateToState';
import { convertWithExchangeRate } from 'selectors/periods';

export class JournalEntryForm extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showErConversion: false,
      selecterErId: undefined,
    };
  }

  handleChange = ({ target }) => {
    const value = target.type === 'checkbox' ? target.checked : target.value;

    this.props.onUpdate({ [target.name]: value });
  };

  handleStatusChange = ({ target }) => {
    this.props.onUpdate({ status: target.name });
  };

  handleRemoveEntryItem = (itemId) => {
    const { journalEntry, onUpdate } = this.props;
    onUpdate({ items: removeProperty(journalEntry.items, itemId) });
  };

  // handle debit or credit change - type is "debit" or "credit"
  handleAmountChange = (itemId, value, type) => {
    const otherType = type === 'debit' ? 'credit' : 'debit';
    const { journalEntry, onUpdate } = this.props;

    onUpdate({
      items: {
        ...journalEntry.items,
        [itemId]: {
          ...journalEntry.items[itemId],
          [type]: currency(value),
          // debit or credit cannot be both non zero
          [otherType]: currency(0),
        },
      },
    });
  };

  /*
   * Check if the value of othertype debit or credit is zero and
   * if not ask confirmation to zero it out.
   */
  handleAmountChangeValidated = (itemId, value, type) => {
    const otherType = type === 'debit' ? 'credit' : 'debit';
    const item = this.props.journalEntry.items[itemId];

    if (parseFloat(value) !== 0 && item[otherType].intValue !== 0) {
      this.props.showConfirmation(
        'Journal entry can have only debit or credit amount, not both. Do you want to zero out the other value?',
        () => this.handleAmountChange(itemId, value, type)
      );
    } else {
      this.handleAmountChange(itemId, value, type);
    }
  };

  handleErConversionChange = (event) => {
    const { target } = event;
    const exchangeRateId = target.value;

    this.setState({ selectedErId: exchangeRateId });
  };

  handleErConversionClick = () => {
    const { openModal } = this.props;

    openModal({
      id: uuid(),
      type: 'confirmation',
      text: 'Are you sure you want to convert?',
      onConfirm: () => {
        this.handleErConversion();
      },
    });
  };

  handleErConversion = () => {
    const { settings, journalEntry, onUpdate } = this.props;
    const { selectedErId } = this.state;

    const { periodId, items } = journalEntry;

    const exchangeRate = (settings?.exchangeRates || []).find(
      (exchangeRate) => exchangeRate.id === selectedErId
    );

    const exchangeRateForPeriod = exchangeRate
      ? exchangeRate[`er-${periodId}`]
      : DEFAULT_EXCHANGE_RATE;

    const newItems = {};

    Object.keys(items).forEach((itemKey) => {
      const item = items[itemKey];

      const creditConverted = convertWithExchangeRate(
        item.credit,
        exchangeRateForPeriod
      );
      const debitConverted = convertWithExchangeRate(
        item.debit,
        exchangeRateForPeriod
      );

      newItems[itemKey] = {
        ...item,
        credit: creditConverted,
        debit: debitConverted,
      };
    });

    onUpdate({ items: newItems });
  };

  getIsNew = () => {
    const { journalEntry } = this.props;
    const { id } = journalEntry;

    return id === -1;
  };

  renderPeriod = (p) => (
    <option key={p.id} value={p.id}>
      {p.name}
    </option>
  );

  renderItem = ([itemId, item]) => {
    const { settings } = this.props;
    return (
      <JournalEntryItemForm
        key={itemId}
        id={itemId}
        item={item}
        onChange={this.handleAmountChangeValidated}
        onRemove={this.handleRemoveEntryItem}
        settings={settings}
      />
    );
  };

  renderErCheckbox = () => {
    const { journalEntry } = this.props;
    const value = !!journalEntry.er;
    const type = journalEntry.type;

    if (type !== 'aje') {
      return null;
    }

    return (
      <div className="exchangeRateCheckbox">
        <label htmlFor="er">Is for Exchange Rate adjustment?</label>
        <input
          type="checkbox"
          id="er"
          name="er"
          checked={value}
          value={value}
          onChange={this.handleChange}
        />
      </div>
    );
  };

  renderErConversionForm = () => {
    const { journalEntry, choices } = this.props;
    const { showErConversion, selectedErId } = this.state;

    const type = journalEntry.type;

    if (type !== 'cje' || this.getIsNew()) {
      return null;
    }

    const defaultExchangeRateLabel = 'Default Exchange Rate';
    const choicesToDisplay = {
      [defaultExchangeRateLabel]: '',
      ...choices,
    };

    return (
      <div className="exchangeRateCheckbox">
        <label htmlFor="er">Convert to another Exchange Rate</label>
        <input
          type="checkbox"
          id="showErConversion"
          name="showErConversion"
          checked={showErConversion}
          value={showErConversion}
          onChange={() =>
            this.setState((prevState) => ({
              showErConversion: !prevState.showErConversion,
            }))
          }
        />
        {showErConversion && (
          <>
            <div>
              <select
                className="exchangeRateJournalDropdown"
                name="exchangeRateCellRendererDropdown"
                value={selectedErId}
                onChange={this.handleErConversionChange}
              >
                {map(choicesToDisplay, (value, key) => (
                  <option key={key} value={value}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                type="button"
                className="btn exchangeRateJournalButton"
                onClick={this.handleErConversionClick}
              >
                Convert
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  renderItemsTable = () => {
    const { settings } = this.props;
    const { items } = this.props.journalEntry;
    const hasItems = !!Object.keys(items).length;
    const currencyValueFormatterWithSettings = currencyValueFormatter(settings);

    return (
      <table className="journal-items">
        <thead>
          <tr>
            <th className="description">Account / Group</th>
            <th className="debit">Debit</th>
            <th className="credit">Credit</th>
            <th />
          </tr>
        </thead>
        {hasItems && (
          <tfoot>
            <tr>
              <th className="description">Total</th>
              <th className="debit">
                {currencyValueFormatterWithSettings(
                  currencySum(Object.values(items).map((i) => i.debit))
                )}
              </th>
              <th className="credit">
                {currencyValueFormatterWithSettings(
                  currencySum(Object.values(items).map((i) => i.credit))
                )}
              </th>
              <th />
            </tr>
          </tfoot>
        )}
        <tbody>{Object.entries(items).map(this.renderItem)}</tbody>
      </table>
    );
  };

  render() {
    const { periods, journalEntry } = this.props;

    return (
      <form>
        <select
          name="periodId"
          value={journalEntry.periodId}
          onChange={this.handleChange}
        >
          {periods.map(this.renderPeriod)}
        </select>
        <input
          name="description"
          autoComplete="off"
          type="text"
          value={journalEntry.description}
          onChange={this.handleChange}
          placeholder="Entry description"
          onKeyPress={(e) => {
            if (e.which === 13 /* Enter */) {
              e.preventDefault();
            }
          }}
        />
        <Toggle
          options={Object.values(JournalStatus)}
          activeOption={journalEntry.status}
          onChange={this.handleStatusChange}
        />
        {this.renderErCheckbox()}
        {this.renderErConversionForm()}
        {this.renderItemsTable()}
      </form>
    );
  }
}
